// Cell Detail Page - Loads all cell data at once for instant section navigation
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import ChemicalSection from '../components/cell-detail/sections/ChemicalSection.jsx'
import ElectricalSection from '../components/cell-detail/sections/ElectricalSection.jsx'
import FactSheetSection from '../components/cell-detail/sections/FactSheetSection.jsx'
import MechanicalSection from '../components/cell-detail/sections/MechanicalSection.jsx'
import QualitySection from '../components/cell-detail/sections/QualitySection.jsx'
import ThermalSection from '../components/cell-detail/sections/ThermalSection.jsx'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.jsx'
import { getCellForSection, getExplorerProducts, getSignedPictureUrl, trackCellView, trackSectionChange } from '../modules/dataService.js'
import {
    activeSection,
    openDetailTile,
    switchSection
} from '../state/globalSignals.js'
import { getProductIdFromSlug, registerCell } from '../utils/cellSlugMap.js'
import { findCellBySlug } from '../utils/cellSlug.js'
import { isCellUnlockedToday, markCellViewedToday, notifyDetailUnlock } from '../state/demoLimitStore.js'

// API Base URL - use environment variable, fallback to window config, or empty for same-origin
const API_BASE_URL = import.meta.env?.VITE_API_URL || window.BATEMO_API?.baseUrl || ''

// All categories to load when opening a cell page
// Loading all at once enables instant navigation between sections
// Rate limiting is applied per-cell, not per-category, so scraping is still prevented
const ALL_CATEGORIES = ['general', 'electrical', 'mechanical', 'chemical', 'safety', 'thermal', 'batch', 'aging']

// Export for use by UnifiedContextBar to avoid duplicate requests
export { ALL_CATEGORIES as SECTION_CATEGORIES }

export default function CellPage({
  params = {},
  productId: propProductId,
  onBack,
  initialSection = null,
  initialDetail = null
}) {
  // Enable keyboard shortcuts globally
  useKeyboardShortcuts()

  const [, setLocation] = useLocation()

  // Extract productId from params (cellSlug) or use prop
  const cellSlug = params?.cellSlug

  // Try to get productId from slug, or use slug as productId if it looks like a hash
  const [productId, setProductId] = useState(() => {
    if (propProductId) return propProductId
    if (!cellSlug) return null

    // Try slug map first
    const idFromSlug = getProductIdFromSlug(cellSlug)
    if (idFromSlug) return idFromSlug

    // If slug looks like a hash ID (16 hex chars), use it directly
    if (/^[a-f0-9]{16}$/i.test(cellSlug)) {
      return cellSlug.toLowerCase()
    }

    // Otherwise we'll need to resolve it via API
    return null
  })

  const [cellData, setCellData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [imageUrls, setImageUrls] = useState({})

  // Update productId when propProductId changes
  useEffect(() => {
    console.log('[CellPage] propProductId changed:', propProductId)
    if (propProductId && propProductId !== productId) {
      console.log('[CellPage] Updating productId from prop:', productId, '->', propProductId)
      setProductId(propProductId)
      setCellData(null) // Reset cell data to force reload
      setLoading(true)
    }
  }, [propProductId])

  // Resolve productId from slug via API if needed
  useEffect(() => {
    if (productId || !cellSlug) return

    // Try to find productId by searching via explorer endpoint (FAST!)
    // The explorer endpoint returns minimal data: product_id, name, manufacturer
    async function resolveSlugViaExplorer() {
      try {
        console.log('[CellPage] 🔍 Resolving slug via explorer search:', cellSlug)

        // Parse slug to extract potential search terms
        // Slug format: "manufacturer-name" → "manufacturer name"
        const searchTerms = cellSlug.replace(/-/g, ' ').toLowerCase()

        // Use explorer endpoint with search - returns minimal data, fast!
        const result = await getExplorerProducts({
          page: 1,
          limit: 50,
          search: searchTerms
        })

        const items = result.items || []
        console.log('[CellPage] Explorer search returned', items.length, 'candidates')

        // Register candidates for future lookups
        for (const item of items) {
          registerCell(item.product_id, {
            manufacturer: item.manufacturer,
            name: item.name
          })
        }

        // Find the matching product by slug (a pre-#18 legacy slug matches
        // too, so bookmarked links keep resolving)
        const match = findCellBySlug(items, cellSlug)
        if (match) {
          console.log('[CellPage] ✓ Found productId for slug:', cellSlug, '->', match.product_id)
          setProductId(match.product_id)
          return
        }

        // If search didn't find exact match, try loading more pages
        if (result.has_next && result.total > items.length) {
          console.log('[CellPage] Checking additional pages...')

          // Try a few more pages
          for (let page = 2; page <= Math.min(5, result.pages); page++) {
            const moreResults = await getExplorerProducts({
              page,
              limit: 50,
              search: searchTerms
            })

            const moreItems = moreResults.items || []
            for (const item of moreItems) {
              registerCell(item.product_id, {
                manufacturer: item.manufacturer,
                name: item.name
              })
            }

            const moreMatch = findCellBySlug(moreItems, cellSlug)
            if (moreMatch) {
              console.log('[CellPage] ✓ Found productId on page', page, ':', cellSlug, '->', moreMatch.product_id)
              setProductId(moreMatch.product_id)
              return
            }
          }
        }

        // Not found
        console.warn('[CellPage] ❌ Could not find cell with slug:', cellSlug)
        setError(`Could not find cell: ${cellSlug.replace(/-/g, ' ')}`)
        setLoading(false)
      } catch (err) {
        console.error('[CellPage] Failed to resolve slug:', err)
        setError(`Error loading cell: ${err.message}`)
        setLoading(false)
      }
    }

    resolveSlugViaExplorer()
  }, [cellSlug, productId])

  // Show error if no productId could be determined
  if (error || (!productId && !loading)) {
    return (
      <div className="cell-page-error">
        <h2>Cell Not Found</h2>
        <p>{error || `Could not find cell with slug: ${cellSlug}`}</p>
        <button onClick={() => setLocation('/explorer')}>Back to Explorer</button>
      </div>
    )
  }

  if (!productId) {
    return (
      <div className="cell-page-loading">
        <p>Resolving cell...</p>
      </div>
    )
  }

  // Load ALL cell data at once when opening a cell page
  // This enables instant navigation between sections (factsheet, electrical, mechanical, etc.)
  // Rate limiting is per-cell access, so scraping all cells is still prevented
  useEffect(() => {
    if (!productId) {
      console.warn('[CellPage] No productId provided, skipping data load')
      return
    }

    console.log('[CellPage] Loading all cell data for productId:', productId)
    setLoading(true)

    // Detail-page unlock auto-refetch (Task A1): capture the cell's
    // unlocked-today state BEFORE this load starts — i.e. before
    // markCellViewedToday/refreshDemoUsageSummary below can add it to
    // viewedToday — so notifyDetailUnlock can tell a genuine new-unlock
    // transition apart from a re-open of an already-unlocked cell.
    const wasUnlockedBeforeLoad = isCellUnlockedToday(productId)

    // Load all categories at once for instant section switching
    getCellForSection(productId, ALL_CATEGORIES)
      .then((data) => {
        console.log('[CellPage] ✓ All cell data loaded:', data?.manufacturer, data?.name)
        setCellData(data)
        setLoading(false)

        // Register cell for slug mapping
        registerCell(productId, data)
        // Update page title
        updatePageTitle(data, activeSection.value)
        // Load images with authentication (pass format_group to determine image count)
        loadImages(productId, data?.mechanical?.format_group || '')

        // Track cell view in Plausible Analytics
        trackCellView(productId, `${data?.manufacturer || ''} ${data?.name || ''}`.trim(), 'detail')

        // A successful open may have consumed a DEMO deep-dive (first view of a
        // new cell/day). Optimistically record THIS cell as viewed-today right
        // now (idempotent, no-op for non-demo users) so the grid lock-affordance
        // keeps it unlocked immediately — before, and independent of, the async
        // usage-summary round-trip below (which can race/overwrite).
        markCellViewedToday({
          product_id: productId,
          name: data?.name,
          manufacturer: data?.manufacturer,
        })

        // Re-sync the meter from the authoritative usage-summary (no-op for
        // non-demo users). Source of truth = the endpoint (count + reset).
        // Once that resolves, notifyDetailUnlock decides — from the
        // wasUnlockedBeforeLoad snapshot above — whether this load was a
        // GENUINE new unlock via the detail page; if so it bumps
        // demoUnlockVersion so MainAppPage's reactive effect reloads the
        // explorer, matching the in-place unlock's auto-refetch.
        import('../services/demoLimitService.js')
          .then(({ refreshDemoUsageSummary }) => refreshDemoUsageSummary(true))
          .then(() => notifyDetailUnlock(productId, wasUnlockedBeforeLoad))
          .catch(() => { /* meter degrades to localStorage mirror — non-fatal */ })
      })
      .catch((err) => {
        console.error('[CellPage] Failed to load cell data:', err)
        // DEMO daily view-limit (Slice A/B): open the conversion overlay (or a
        // slim toast on repeat hits) and show a calm in-place placeholder
        // instead of the generic error page. 429s are never cached, so this is
        // safe to re-trigger.
        if (err?.code === 'DEMO_LIMIT' || err?.detail?.code === 'demo_daily_view_limit') {
          // The global DailyLimitModal is already open (central interceptor in
          // dataService.getCellNormalized). Do NOT leave the user on an empty cell
          // page — MainAppPage unmounts the grid when ?cell= is set, so a blank
          // placeholder is all that renders. Navigate back to the catalog so the
          // modal overlays the (always-free) grid, per Masterplan Bug A.
          try {
            const params = new URLSearchParams(window.location.search)
            params.delete('cell')
            params.delete('section')
            const qs = params.toString()
            setLocation(`/explorer${qs ? `?${qs}` : ''}`)
          } catch (_) {
            setLocation('/explorer')
          }
          setLoading(false)
          return
        }
        const msg = err?.message || 'Failed to load cell data'
        if (msg.includes('403') || msg.includes('Demo users can only')) {
          setError('This cell is not included in your plan. Upgrade to PRO for full access.')
        } else if (msg.includes('429') || msg.includes('limit')) {
          setError('Too many requests. Please try again in a few minutes.')
        } else {
          setError(msg)
        }
        setLoading(false)
      })
  }, [productId])

  // Update page title when section changes
  useEffect(() => {
    if (cellData) {
      updatePageTitle(cellData, activeSection.value)
    }

    // Cleanup: Reset title when component unmounts
    return () => {
      document.title = 'Batemo Insights'
    }
  }, [activeSection.value, cellData])

  // Function to update page title
  function updatePageTitle(data, section) {
    if (!data) return

    const manufacturer = data.manufacturer || 'Unknown'
    const name = data.name || 'Battery Cell'

    // Section name mapping (user-friendly)
    const sectionNames = {
      'scores': 'Scores',
      'electrical': 'Electrical',
      'mechanical': 'Mechanical',
      'thermal': 'Thermal',
      'chemical': 'Chemical',
      'quality': 'Quality',
      'factsheet': 'Fact Sheet'
    }

    const sectionName = sectionNames[section] || ''

    // Build title
    if (sectionName) {
      document.title = `${manufacturer} ${name} - ${sectionName} | Batemo Insights`
    } else {
      document.title = `${manufacturer} ${name} | Batemo Insights`
    }
  }

  // Get current location for URL change detection
  const [currentLocation] = useLocation()

  // Initialize section/detail from URL parameters and watch for changes
  useEffect(() => {
    // Check URL for section parameter
    const url = new URL(window.location)
    const urlSection = url.searchParams.get('section')

    const section = params?.section || urlSection || initialSection || 'factsheet'
    const detail = params?.detail || initialDetail

    console.log('[CellPage] Section sync:', { urlSection, section, activeSection: activeSection.value, currentLocation })

    // Always update section when URL changes (not just when different from current)
    if (section) {
      console.log('[CellPage] Switching section to:', section)
      switchSection(section)

      // Track section change in Plausible Analytics
      if (productId) {
        trackSectionChange(section, productId)
      }
    }

    if (detail && section) {
      // Small delay to ensure section is loaded
      setTimeout(() => {
        openDetailTile(detail)
      }, 100)
    }
  }, [params?.section, params?.detail, initialSection, initialDetail, currentLocation])

  // NOTE: URL syncing is handled by UnifiedContextBar now (query param based)
  // Don't sync URL here to avoid conflicts with the new /explorer?cell= pattern

  async function loadImages(prodId, formatGroup = '') {
    // Pouch cells only have 1 image, cylindrical and prismatic have 2
    const isPouch = formatGroup && formatGroup.toLowerCase().includes('pouch')
    const maxImages = isPouch ? 1 : 2

    const urls = {}
    for (let i = 1; i <= maxImages; i++) {
      const imageName = `website_picture_cell${i}.png`
      // Use signed URLs for direct browser caching (more performant than base64)
      const signedUrl = await getSignedPictureUrl(prodId, imageName)
      if (signedUrl) {
        urls[i] = signedUrl
      }
    }
    setImageUrls(urls)
  }

  const openLightbox = (index = 0) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % 2)
  }

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + 2) % 2)
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      setLocation('/')
    }
  }

  // Publish cell data for CommandBar (SingleCellCommandBar needs it)
  useEffect(() => {
    if (cellData) {
      if (typeof window !== 'undefined') {
        window.__ACTIVE_CELL__ = {
          data: cellData,
          imageUrl: imageUrls[1],
          slug: cellSlug
        }
        window.dispatchEvent(new Event('active-cell-changed'))
      }
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.__ACTIVE_CELL__ = null
        window.dispatchEvent(new Event('active-cell-changed'))
      }
    }
  }, [cellData, imageUrls, cellSlug])

  if (loading) return <div className="loading">Loading cell data...</div>

  if (error || !cellData) {
    return (
      <div className="cell-page-error" style="padding: 40px; text-align: center;">
        <h2 style="color: #ea5b0c; margin-bottom: 12px;">Unable to Load Cell</h2>
        <p style="color: #666; margin-bottom: 20px;">{error || 'Cell data could not be loaded.'}</p>
        <button
          onClick={() => setLocation('/explorer')}
          style="padding: 10px 20px; background: #ea5b0c; color: white; border: none; border-radius: 4px; cursor: pointer;"
        >
          Back to Explorer
        </button>
      </div>
    )
  }

  return (
    <div className="cell-detail-page" data-tour="cell-detail-page">
      {/* NO DrawerHeader - CommandBar handles it now */}

      <main className="cell-detail-content" data-tour="cell-detail-content">
        {/* Render active section */}
        {activeSection.value === 'factsheet' && <FactSheetSection cellData={cellData} imageUrl={imageUrls[1]} />}
        {activeSection.value === 'electrical' && <ElectricalSection cellData={cellData} />}
        {activeSection.value === 'thermal' && <ThermalSection cellData={cellData} />}
        {activeSection.value === 'mechanical' && <MechanicalSection cellData={cellData} imageUrl={imageUrls[1]} />}
        {activeSection.value === 'chemical' && <ChemicalSection cellData={cellData} />}
        {activeSection.value === 'quality' && <QualitySection cellData={cellData} />}
      </main>

      {/* Lightbox for cell images */}
      {lightboxOpen && imageUrls[lightboxIndex + 1] && (
        <div className="lightbox open" onClick={closeLightbox}>
          <figure onClick={(e) => e.stopPropagation()}>
            <img
              src={imageUrls[lightboxIndex + 1]}
              alt={`${cellData?.name} - Image ${lightboxIndex + 1}`}
            />
            <button className="lb-close" onClick={closeLightbox}>×</button>
            {lightboxIndex > 0 && <button className="lb-prev" onClick={prevImage}>‹</button>}
            {lightboxIndex < 1 && <button className="lb-next" onClick={nextImage}>›</button>}
            <div className="lb-count">{lightboxIndex + 1} / 2</div>
          </figure>
        </div>
      )}
    </div>
  )
}
